import * as React from 'react';

import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
	Badge,
	Box,
	Button,
	Collapse,
	IconButton,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	Paper,
	Stack,
	Tooltip,
	Typography,
	useTheme,
} from '@mui/material';

import { CodeBaseStatesModel } from '../../../api/api';
import CopyToClipboardButton from '../../../components/Clipboard/CopyToClipboardButton';

const STAGES: CodeBaseStatesModel[] = [CodeBaseStatesModel.Changed, CodeBaseStatesModel.Packaged, CodeBaseStatesModel.Deployed];

export interface DeploymentStatusProps {
	state: CodeBaseStatesModel;
	size?: number;
	gap?: number;
	showLabels?: boolean;
	onAdvance?: (next: CodeBaseStatesModel, current: CodeBaseStatesModel) => void | Promise<void>;
	hideWhenDone?: boolean;
	disabled?: boolean;
	/** Optional: show loading indicator while operation is in progress */
	loading?: boolean;
	variant?: 'default' | 'header';
	operationErrors?: string[] | null;
	operationErrorTitle?: React.ReactNode;
	operationErrorSubtitle?: React.ReactNode;
}

export default function DeploymentStatus({
	state,
	size,
	gap,
	showLabels = false,
	onAdvance,
	hideWhenDone = true,
	disabled = false,
	loading = false,
	variant = 'default',
	operationErrors,
	operationErrorTitle = 'Operation failed',
	operationErrorSubtitle = 'Review the errors below and try again.',
}: DeploymentStatusProps) {
	const theme = useTheme();
	const [errorsOpen, setErrorsOpen] = React.useState(false);
	const isHeader = variant === 'header';
	const effectiveSize = size ?? (isHeader ? 10 : 14);
	const effectiveGap = gap ?? (isHeader ? 10 : 12);
	const failedMarkerSize = Math.max(effectiveSize + 10, 20);
	const stageCellWidth = isHeader ? 58 : 72;
	const currentIndex = Math.max(0, STAGES.indexOf(state));
	const nextState = state === CodeBaseStatesModel.Deployed ? CodeBaseStatesModel.Deployed : STAGES[currentIndex + 1];
	const hasOperationErrors = !!operationErrors?.length;
	const failedIndex = hasOperationErrors ? Math.min(currentIndex + 1, STAGES.length - 1) : -1;

	React.useEffect(() => {
		setErrorsOpen(hasOperationErrors);
	}, [hasOperationErrors, operationErrors, operationErrorTitle, operationErrorSubtitle]);

	const colorFor = (i: number) => {
		if (i === failedIndex) {
			return theme.palette.error.main;
		}

		if (i <= currentIndex) {
			return theme.palette.success.main;
		}

		return theme.palette.divider;
	};

	const connectorColorFor = (i: number) => {
		if (i < currentIndex) {
			return theme.palette.success.main;
		}

		if (i === currentIndex && failedIndex === i + 1) {
			return theme.palette.error.main;
		}

		return theme.palette.divider;
	};

	const actionLabel = (() => {
		switch (state) {
			case CodeBaseStatesModel.Changed:
				return 'Package';
			case CodeBaseStatesModel.Packaged:
				return 'Deploy';
			case CodeBaseStatesModel.Deployed:
				return 'ReDeploy';
			default:
				return '';
		}
	})();

	const hasNextState = nextState !== undefined;
	const showAction = hasNextState || !hideWhenDone;

	const handleClick = async () => {
		if (nextState === undefined || !onAdvance || loading) return;
		await onAdvance(nextState, state);
	};

	return (
		<Box
			sx={{
				width: isHeader ? 'auto' : '100%',
				minWidth: 0,
				display: 'flex',
				alignItems: 'center',
				justifyContent: isHeader ? 'flex-end' : 'flex-end',
				gap: isHeader ? 1 : 1.5,
				py: 0.5,
				px: 0,
				position: 'relative',
			}}
		>
			{/* LEFT: indicator */}
			<Stack spacing={0} alignItems="stretch" sx={{ display: 'inline-flex', flexShrink: 0 }}>
				<Stack
					direction="row"
					alignItems="center"
					sx={{
						display: 'grid',
						gridTemplateColumns: `${stageCellWidth}px ${effectiveGap}px ${stageCellWidth}px ${effectiveGap}px ${stageCellWidth}px`,
						alignItems: 'center',
						userSelect: 'none',
					}}
					role="list"
					aria-label="Deployment status"
				>
					{STAGES.map((label, i) => {
						const isCurrent = i === currentIndex;
						const isFailed = i === failedIndex;
						const circle = (
							<Box
								role="listitem"
								aria-current={isCurrent ? 'step' : undefined}
								sx={{
									width: effectiveSize,
									height: effectiveSize,
									borderRadius: '50%',
									bgcolor: colorFor(i),
									boxShadow: isCurrent || isFailed ? 0 : 1,
									position: 'relative',
									cursor: isFailed ? 'pointer' : 'default',
									...(isCurrent &&
										!isFailed && {
											'::after': {
												content: '""',
												position: 'absolute',
												inset: -4,
												borderRadius: '50%',
												border: `2px solid ${colorFor(i)}`,
												animation: 'pulse 1.6s ease-out infinite',
											},
											'@keyframes pulse': {
												'0%': { opacity: 0.6, transform: 'scale(0.9)' },
												'70%': { opacity: 0, transform: 'scale(1.4)' },
												'100%': { opacity: 0, transform: 'scale(1.4)' },
											},
										}),
								}}
							/>
						);
						const failedCircle = (
							<Box
								role="listitem"
								aria-current={isCurrent ? 'step' : undefined}
								sx={{
									width: failedMarkerSize,
									height: failedMarkerSize,
									borderRadius: '50%',
									display: 'grid',
									placeItems: 'center',
									border: '1px solid',
									borderColor: 'error.main',
									bgcolor: 'background.paper',
									color: 'error.main',
								}}
							>
								<ErrorOutlineIcon sx={{ fontSize: failedMarkerSize - 6 }} />
							</Box>
						);
						const circleContent = isFailed ? (
							<IconButton
								aria-label="Show operation output"
								size="small"
								onClick={() => setErrorsOpen(true)}
								sx={{
									p: 0,
									width: failedMarkerSize,
									height: failedMarkerSize,
									'&:hover': { bgcolor: 'transparent' },
								}}
							>
								<Badge badgeContent={operationErrors?.length ?? 0} color="error" max={99} overlap="circular">
									{failedCircle}
								</Badge>
							</IconButton>
						) : (
							circle
						);

						return (
							<React.Fragment key={label}>
								<Stack direction="column" alignItems="center" spacing={showLabels ? 0.5 : 0} sx={{ width: stageCellWidth, minWidth: 0 }}>
									<Tooltip title={CodeBaseStatesModel[label] ?? String(label)} arrow>
										{circleContent}
									</Tooltip>
									{showLabels && (
										<Typography
											variant="caption"
											sx={{
												textAlign: 'center',
												fontSize: '0.65rem',
												whiteSpace: 'nowrap',
												color: isFailed ? 'error.main' : i > currentIndex ? 'text.secondary' : undefined,
											}}
										>
											{CodeBaseStatesModel[label] ?? String(label)}
										</Typography>
									)}
								</Stack>
								{i < STAGES.length - 1 && (
									<Box
										aria-hidden
										sx={{
											width: effectiveGap,
											height: 2,
											mt: showLabels ? -1.5 : 0,
											bgcolor: connectorColorFor(i),
											borderRadius: 1,
											opacity: 0.9,
										}}
									/>
								)}
							</React.Fragment>
						);
					})}
				</Stack>
			</Stack>

			{hasOperationErrors && (
				<Box>
					<Collapse in={errorsOpen} unmountOnExit>
						<Paper
							elevation={8}
							sx={{
								position: 'absolute',
								top: 'calc(100% + 8px)',
								right: 0,
								zIndex: (muiTheme) => muiTheme.zIndex.drawer + 2,
								width: 'min(720px, calc(100vw - 48px))',
								maxHeight: 'min(60vh, 520px)',
								overflow: 'hidden',
								border: '1px solid',
								borderColor: 'divider',
								bgcolor: 'background.paper',
							}}
						>
							<Stack
								direction="row"
								alignItems="flex-start"
								justifyContent="space-between"
								spacing={2}
								sx={{
									px: 2,
									py: 1.5,
									bgcolor: (muiTheme) => muiTheme.palette.action.hover,
									borderBottom: '1px solid',
									borderColor: 'divider',
								}}
							>
								<Stack direction="row" spacing={1.25} alignItems="flex-start">
									<ErrorOutlineIcon color="error" fontSize="small" sx={{ mt: 0.25 }} />
									<Box>
										<Stack direction="row" spacing={1} alignItems="center">
											<Typography fontWeight={600}>{operationErrorTitle}</Typography>
											<Typography variant="caption" color="text.secondary">
												{operationErrors?.length ?? 0}
											</Typography>
										</Stack>
										<Typography variant="body2" color="text.secondary">
											{operationErrorSubtitle}
										</Typography>
									</Box>
								</Stack>
								<Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
									<CopyToClipboardButton
										value={operationErrors?.join('\n') ?? ''}
										title="Copy all errors"
										edge={false}
										sx={{ color: 'text.secondary' }}
									/>
									<Tooltip title="Close operation output">
										<IconButton aria-label="Close operation output" size="small" onClick={() => setErrorsOpen(false)}>
											<CloseIcon fontSize="small" />
										</IconButton>
									</Tooltip>
								</Stack>
							</Stack>

							<Box sx={{ maxHeight: '42vh', overflow: 'auto', pr: 1 }}>
								<List dense>
									{operationErrors?.map((message, index) => (
										<ListItem
											key={index}
											alignItems="flex-start"
											secondaryAction={<CopyToClipboardButton value={message} title="Copy error" edge={false} sx={{ mt: -0.5 }} />}
										>
											<ListItemIcon sx={{ minWidth: 40 }}>
												<ErrorOutlineIcon color="error" />
											</ListItemIcon>
											<ListItemText
												slotProps={{ primary: { variant: 'body2' } }}
												primary={message}
												sx={{
													'& .MuiListItemText-primary': {
														overflowWrap: 'anywhere',
													},
												}}
											/>
										</ListItem>
									))}
								</List>
							</Box>
						</Paper>
					</Collapse>
				</Box>
			)}

			{/* RIGHT: action button */}
			{showAction && (
				<Box sx={{ flexShrink: 0 }}>
					{hasNextState ? (
						<Button
							variant="contained"
							size={isHeader ? 'small' : undefined}
							onClick={handleClick}
							disabled={disabled || !onAdvance || loading}
							loading={loading}
							aria-label={`Move to ${CodeBaseStatesModel[nextState] ?? actionLabel}`}
							sx={{ width: '90px', px: 1.5 }}
						>
							{actionLabel}
						</Button>
					) : (
						!hideWhenDone && (
							<Button variant="contained" color="success" size={isHeader ? 'small' : undefined} disabled sx={isHeader ? { minWidth: 0, px: 1.5 } : undefined}>
								Done
							</Button>
						)
					)}
				</Box>
			)}
		</Box>
	);
}
